import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackParentalConsentEvent } from '@/helpers/analytics';
import { getOnboardingGuest, markOnboardingGuestEnrolled } from '@/helpers/onboardingGuest';
import { attachOnboardingSessionId } from '@/helpers/onboardingSession';
import { navigateToError } from '@/helpers/navigateToError';
import {
  createInitialParentalConsentForm,
  isParentalConsentFormValid,
  validateParentalConsentField,
} from '@/Pages/ParentalConsent/hooks/useParentalConsentValidation';
import { parseParentalConsentNavigationState } from '@/Pages/ParentalConsent/types/parentalConsentLocationState';
import { normalizeParentalConsentFormData } from '@/Pages/ParentalConsent/utils/normalizeParentalConsentForm';
import { normalizeSignupFormData } from '@/Pages/Signup/utils/normalizeSignupForm';
import { successNavigationOptions } from '@/Pages/Success/types/successLocationState';
import { api, ApiError } from '@/basedata/apiCall';
import type { ParentalConsentFormData } from '@/types/parental-consent';
import type { MinorWithGuardianSignupPayload, SignupMinorNavigationState } from '@/types/signup';

const FALLBACK_NEXT_STEP = '/success';
const SIGNUP_PATH = '/signup';
const PARENTAL_CONSENT_PATH = '/parental-consent';
// Acompanhantes pendentes: a tela do grupo vive em /invite.
const COMPANIONS_PATH = '/invite';

export type UseParentalConsentFormResult = {
  minorData: SignupMinorNavigationState;
  form: ParentalConsentFormData;
  fieldErrors: Partial<Record<keyof ParentalConsentFormData, string>>;
  touched: Partial<Record<keyof ParentalConsentFormData, boolean>>;
  isSubmitEnabled: boolean;
  isSubmitting: boolean;
  setField: (field: keyof ParentalConsentFormData, value: string | boolean) => void;
  blurField: (field: keyof ParentalConsentFormData) => void;
  submit: () => Promise<void>;
  goBack: () => void;
};

export function useParentalConsentForm(): UseParentalConsentFormResult {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = useMemo(
    () => parseParentalConsentNavigationState(location.state),
    [location.state],
  );
  const minorData = navigationState.minorData ?? {
    fullName: '',
    email: '',
    birthDate: '',
    nationality: '',
    document: '',
  };
  const [form, setForm] = useState<ParentalConsentFormData>(() =>
    navigationState.form ?? createInitialParentalConsentForm(),
  );
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof ParentalConsentFormData, string>>
  >({});
  const [touched, setTouched] = useState<Partial<Record<keyof ParentalConsentFormData, boolean>>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmitEnabled = isParentalConsentFormValid(form);
  const setField = useCallback((field: keyof ParentalConsentFormData, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);
  const blurField = useCallback((field: keyof ParentalConsentFormData) => {
    setTouched((current) => ({ ...current, [field]: true }));
    setForm((current) => {
      const error = validateParentalConsentField(field, current);
      setFieldErrors((errors) => {
        if (!error) {
          const { [field]: _removed, ...rest } = errors;
          return rest;
        }
        return { ...errors, [field]: error };
      });
      return current;
    });
  }, []);
  const submit = useCallback(async () => {
    if (!isParentalConsentFormValid(form)) {
      const fields: (keyof ParentalConsentFormData)[] = [
        'guardianFullName',
        'guardianEmail',
        'guardianCpf',
        'guardianPhone',
        'guardianRelationship',
        'consentGiven',
      ];
      const nextTouched = fields.reduce<Partial<Record<keyof ParentalConsentFormData, boolean>>>(
        (accumulator, field) => ({ ...accumulator, [field]: true }),
        {},
      );
      const nextErrors = fields.reduce<Partial<Record<keyof ParentalConsentFormData, string>>>(
        (accumulator, field) => {
          const error = validateParentalConsentField(field, form);
          if (error) {
            return { ...accumulator, [field]: error };
          }
          return accumulator;
        },
        {},
      );
      setTouched(nextTouched);
      setFieldErrors(nextErrors);
      return;
    }
    setIsSubmitting(true);

    void trackParentalConsentEvent({ name: 'parental_consent_submit_click' });

    const normalizedMinor = normalizeSignupFormData(minorData);
    const normalizedGuardian = normalizeParentalConsentFormData(form);
    const payload: MinorWithGuardianSignupPayload = {
      ...normalizedMinor,
      ...normalizedGuardian,
      consentGiven: true,
    };
    try {
      const response = await api.signup.submit(attachOnboardingSessionId(payload));
      // Biometria cadastrada: sela o estado ANTES de navegar, senão voltar no
      // histórico devolve a pessoa para a selfie (e cobra outro liveness).
      markOnboardingGuestEnrolled(response.enrolledAt ?? null);
      // Modo evento: TITULAR com acompanhantes pendentes captura antes do
      // sucesso; dependente (role companion, via link único) vai direto ao fim.
      const onboardingGuest = getOnboardingGuest();
      const pendingCompanions =
        onboardingGuest?.role === 'companion' ? 0 : (onboardingGuest?.companions.pending ?? 0);
      const nextStep =
        pendingCompanions > 0
          ? COMPANIONS_PATH
          : response.nextStep.trim() || FALLBACK_NEXT_STEP;
      void trackParentalConsentEvent({
        name: 'parental_consent_submit_success',
        params: { next_step: nextStep },
      });
      navigate(nextStep, successNavigationOptions);
    } catch (error) {
      void trackParentalConsentEvent({ name: 'parental_consent_submit_error' });
      navigateToError(navigate, {
        error,
        originPath: PARENTAL_CONSENT_PATH,
        originState: { minorData, form },
        ...(error instanceof ApiError ? { errorCode: String(error.status) } : {}),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [form, minorData, navigate]);
  const goBack = useCallback(() => {
    void trackParentalConsentEvent({ name: 'parental_consent_back_click' });
    navigate(SIGNUP_PATH, { state: minorData });
  }, [minorData, navigate]);
  return {
    minorData,
    form,
    fieldErrors,
    touched,
    isSubmitEnabled,
    isSubmitting,
    setField,
    blurField,
    submit,
    goBack,
  };
}
